// src/components/chat/MentionAutocomplete.jsx
// Floating dropdown that shows filtered room member list for @mentions.
import React, { useState, useEffect, useCallback, useRef } from 'react';

// No cap — the dropdown scrolls via max-h + overflow-y-auto

export default function MentionAutocomplete({ members, filter, onSelect, onClose }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef(null);

  // Filter members by partial text (case-insensitive)
  const filtered = (Array.isArray(members) ? members : []).filter((m) => {
    if (!filter) return true; // show all when just "@" typed
    return (m.displayName || '').toLowerCase().startsWith(filter.toLowerCase());
  });

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filter]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(filtered.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filtered.length) % Math.max(filtered.length, 1));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (filtered[selectedIndex]) {
        onSelect(filtered[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [filtered, selectedIndex, onSelect, onClose]);

  // Attach keyboard listener to document so it captures from the textarea
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const item = listRef.current.children[selectedIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  if (filtered.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 mb-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto"
    >
      {filtered.map((member, i) => (
        <button
          key={member.mxid || member.userId || i}
          type="button"
          className={
            'w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors ' +
            (i === selectedIndex ? 'bg-[#00aef7]/10' : '')
          }
          onMouseDown={(e) => {
            e.preventDefault(); // prevent textarea blur
            onSelect(member);
          }}
          onMouseEnter={() => setSelectedIndex(i)}
        >
          {/* Avatar circle */}
          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[10px] font-semibold text-gray-500 flex-shrink-0 overflow-hidden">
            {member.avatarUrl ? (
              <img src={member.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              (member.displayName || '?').charAt(0).toUpperCase()
            )}
          </div>
          <span className="truncate">{member.displayName || member.userId || 'Unknown'}</span>
        </button>
      ))}
    </div>
  );
}
